import { BaseErrorResponse } from './errors';
import { BaseApiError } from './types';

export type ApiResponse<T = any> = {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
};

/**
 * Error thrown for non-2xx fetch responses.
 * Keeps an axios-like `response` shape so existing catch blocks that read
 * `error.response?.data` continue to work.
 */
export class BaseRequestError<T = any> extends Error {
  fetchResponse: Response;
  data: T;
  status: number;
  response: {
    data: T;
    status: number;
    statusText: string;
    headers: Headers;
  };

  constructor(
    message: string,
    fetchResponse: Response,
    data: T,
  ) {
    super(message);
    // Babel/TS Error subclassing can break `instanceof` across module copies;
    // set the prototype explicitly and prefer duck-typing in isRequestError.
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'BaseRequestError';
    this.fetchResponse = fetchResponse;
    // Non-JSON bodies (proxy/LB HTML 500s) parse as null via `.json().catch(() => null)`.
    // Normalize so callers can safely read `error.response?.data?.errors` without crashing.
    const normalizedData = (data ?? ({} as T)) as T;
    this.data = normalizedData;
    this.status = fetchResponse.status;
    this.response = {
      data: normalizedData,
      status: fetchResponse.status,
      statusText: fetchResponse.statusText,
      headers: fetchResponse.headers,
    };
  }
}

export function isRequestError(error: unknown): error is BaseRequestError {
  if (error instanceof BaseRequestError) {
    return true;
  }

  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: string }).name === 'BaseRequestError' &&
    'response' in error
  );
}

export class StripeProcessorErrorResponse extends BaseErrorResponse {
  constructor(public message: string, public fatal: boolean = false) {
    super(message, fatal, 'StripeProcessorErrorResponse');
  }
}

export class StoreMembershipErrorResponse extends BaseErrorResponse {
  constructor(public message: string, public fatal: boolean = false) {
    super(message, fatal, 'StoreMembershipErrorResponse');
  }
}

export function handleError<T extends BaseApiError>(
  error: T,
): BaseErrorResponse {
  switch (error.error_class) {
    case 'StripeProcessor::ApiExceptions::NotFoundError':
      return new StripeProcessorErrorResponse(
        'There was a problem handling the request, please try again',
        false,
      );
    case 'StripeProcessor::ApiExceptions::PriceNotFoundError':
      return new StripeProcessorErrorResponse(
        'Product not properly configured in Stripe',
        true,
      );
    default:
      return new StoreMembershipErrorResponse(error.message, false);
  }
}
